import { Breadcrumbs, Icon } from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { useBuildingAdminOrReceptionistAccess } from "@/hooks/useReceptionistAccess";
import PageBackButton from "@/components/PageBackButton";
import { useWelcomeCommunicationAccess } from "@/hooks/useWelcomeCommunicationAccess";
import ArticleForm from "@/components/communication/ArticleForm";
import { createArticle } from "@/api";
import { ArticleSubmitType, NotificationType } from "@/types";
import { useSnackbarStore } from "@/store/zustandStore";

export const Route = createFileRoute("/_portal/building/communication/new-article")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addNotification } = useSnackbarStore();

  const { hasCommunication } = useWelcomeCommunicationAccess();
  const { allowBuildingAdminOrReceptionistAccess } =
    useBuildingAdminOrReceptionistAccess();

  const breadcrumbs = [
    {
      label: t("SIDEBAR.BUILDING_COMMUNICATION"),
      path: `/building/communication`,
    },
    {
      label: t("COMMUNICATION.NEW_ARTICLE"),
      path: `/building/communication/new-article`,
    },
  ];

  const handleSaveDraft = async (values: ArticleSubmitType) => {
    try {
      const res = await createArticle(values);
      addNotification(
        t("ARTICLE.NOTIFICATION_MESSAGES.SAVE_DRAFT_SUCCESS"),
        NotificationType.SUCCESS,
      );
      navigate({
        to: `/building/communication/$articleId`,
        params: { articleId: res.id },
      });
    } catch (error) {
      console.error(error);
      addNotification(t("ARTICLE.NOTIFICATION_MESSAGES.SAVE_DRAFT_ERROR"));
    }
  };

  if (!hasCommunication || !allowBuildingAdminOrReceptionistAccess) {
    return null;
  }

  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton
          fallbackPath={{
            to: "/building/communication",
          }}
        />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{t("PAGES.NEWS.NEW.HEADER")}</h1>
        <p>
          {t("PAGES.NEWS.NEW.SUBHEADER")}
          <br />
          <span className="aiGradientText">
            <Trans
              i18nKey="ARTICLE.GENERATE_CONTENT.TRY_AI_TOOLS"
              components={{
                icon: (
                  <Icon
                    name="book_spark"
                    style={{ verticalAlign: "middle", margin: "0 3px" }}
                  />
                ),
              }}
            />{" "}
            {t("FREE_FOR_LIMITED_TIME").toLowerCase()}
          </span>
        </p>
      </div>
      <ArticleForm onSave={handleSaveDraft} />
    </>
  );
}
